import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Search, Refresh, GetApp, ArrowBack, Close } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar, InputAdornment, TextField } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const fmtQty = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '-';
  const s = String(d).split('T')[0];
  const parts = s.split('-');
  if (parts.length === 3 && parts[0].length === 4) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return s;
};

const fmt2Date = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const PRIMARY = '#4a148c';
const PRIMARY_MID = '#8e24aa';
const PRIMARY_LT = '#f3e5f5';
const th  = { padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)' };
const td  = { padding: '7px 10px', textAlign: 'left', fontSize: 12, whiteSpace: 'nowrap' };
const tdR = { padding: '7px 10px', textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap' };

const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const ProductionWiseStockReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fetched, setFetched]   = useState(false);
  const [search, setSearch]     = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData(
        'StockReportApi',
        `/ProductionWiseStockAnalysis/${companyId}/${branchId}/${fromDate}/${toDate}`,
        token
      );
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch production wise stock report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  // Group data by ProductionId
  const grouped = useMemo(() => {
    const map = {};
    data.forEach(r => {
      const key = r.ProductionId;
      if (!map[key]) {
        map[key] = {
          ProductionId: r.ProductionId,
          VoucherNumber: r.VoucherNumber,
          ProductionDate: r.ProductionDate,
          ProductId: r.ProductId,
          ProductCode: r.ProductCode,
          ProductName: r.ProductName,
          ProducedQty: r.ProducedQty,
          consumedItems: []
        };
      }
      if (r.ConsumedItemId) {
        map[key].consumedItems.push({
          ConsumedItemId: r.ConsumedItemId,
          ConsumedItemCode: r.ConsumedItemCode,
          ConsumedItemName: r.ConsumedItemName,
          ConsumedQty: r.ConsumedQty
        });
      }
    });
    return Object.values(map);
  }, [data]);

  const filtered = useMemo(() => {
    if (!search.trim()) return grouped;
    const q = search.toLowerCase();
    return grouped.filter(r =>
      (r.ProductName || '').toLowerCase().includes(q) ||
      (r.ProductCode || '').toLowerCase().includes(q) ||
      (r.VoucherNumber || '').toLowerCase().includes(q)
    );
  }, [grouped, search]);

  // Totals
  const totalProduced = filtered.reduce((s, r) => s + (parseFloat(r.ProducedQty) || 0), 0);
  const totalConsumed = filtered.reduce((s, r) => s + r.consumedItems.reduce((cs, c) => cs + (parseFloat(c.ConsumedQty) || 0), 0), 0);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'ProductionWiseStock', 0);
    if (!filtered.length) return;
    const cols = ['#', 'Voucher No', 'Date', 'Product Code', 'Product Name', 'Produced Qty', 'Consumed Item Code', 'Consumed Item Name', 'Consumed Qty'];
    const rows = [];
    let idx = 1;
    filtered.forEach(product => {
      if (product.consumedItems.length === 0) {
        rows.push([idx++, product.VoucherNumber || '', fmtDate(product.ProductionDate), product.ProductCode || '', product.ProductName || '', parseFloat(product.ProducedQty) || 0, '', '', '']);
      } else {
        product.consumedItems.forEach((c, ci) => {
          rows.push([
            ci === 0 ? idx++ : '',
            ci === 0 ? (product.VoucherNumber || '') : '',
            ci === 0 ? fmtDate(product.ProductionDate) : '',
            ci === 0 ? (product.ProductCode || '') : '',
            ci === 0 ? (product.ProductName || '') : '',
            ci === 0 ? (parseFloat(product.ProducedQty) || 0) : '',
            c.ConsumedItemCode || '',
            c.ConsumedItemName || '',
            parseFloat(c.ConsumedQty) || 0
          ]);
        });
      }
    });

    const totalsRow = ['', '', '', '', 'TOTAL', totalProduced, '', '', totalConsumed];

    await exportStyledExcel({
      reportTitle: 'PRODUCTION WISE STOCK REPORT',
      sheetName: 'Production Stock',
      fileName: `ProductionWiseStockReport_${fromDate}_${toDate}.xlsx`,
      companyName,
      username,
      fromDate,
      toDate,
      headers: cols,
      rows,
      totalsRow,
      columnWidths: [4, 16, 12, 14, 28, 14, 14, 28, 14],
    });
  };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading production wise stock report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #6a1b9a 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Production Wise Stock Report</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          {searchOpen ? (
            <>
              <TextField autoFocus size="small" placeholder="Search..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} /></InputAdornment>,
                  endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} sx={{ color: 'rgba(255,255,255,0.6)', p: '2px' }}><Close sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
                }}
                sx={{ flexShrink: 0, width: 200, '& .MuiOutlinedInput-root': { height: 29, fontSize: 12, borderRadius: '8px', color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.7)' } }, '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 } }}
              />
              <Tooltip title="Close" arrow>
                <IconButton size="small" onClick={() => { setSearchOpen(false); setSearch(''); }} sx={iconBtn}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Search" arrow>
              <IconButton size="small" onClick={() => setSearchOpen(true)} sx={iconBtn}>
                <Search sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate}   onChange={e => setToDate(e.target.value)}   style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 800 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: PRIMARY_MID, color: 'white' }}>
                <th style={th}>#</th>
                <th style={th}>Voucher No</th>
                <th style={th}>Date</th>
                <th style={th}>Product Code</th>
                <th style={th}>Product Name</th>
                <th style={{ ...th, textAlign: 'right' }}>Produced Qty</th>
                <th style={th}>Consumed Code</th>
                <th style={th}>Consumed Item</th>
                <th style={{ ...th, textAlign: 'right' }}>Consumed Qty</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Select date range and click refresh'}
                  </td>
                </tr>
              ) : (
                filtered.flatMap((product, i) => {
                  if (product.consumedItems.length === 0) {
                    return [
                      <tr key={`p-${product.ProductionId}`} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #f1f5f9' }}>
                        <td style={td}>{i + 1}</td>
                        <td style={{ ...td, fontWeight: 600, color: PRIMARY }}>{product.VoucherNumber || '-'}</td>
                        <td style={td}>{fmtDate(product.ProductionDate)}</td>
                        <td style={{ ...td, fontWeight: 600 }}>{product.ProductCode || '-'}</td>
                        <td style={{ ...td, color: '#1e293b' }}>{product.ProductName || '-'}</td>
                        <td style={{ ...tdR, fontWeight: 700 }}>{fmtQty(product.ProducedQty)}</td>
                        <td style={td}>-</td>
                        <td style={td}>-</td>
                        <td style={tdR}>-</td>
                      </tr>
                    ];
                  }
                  return product.consumedItems.map((c, ci) => (
                    <tr key={`p-${product.ProductionId}-c-${ci}`} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: ci === product.consumedItems.length - 1 ? '2px solid #e2e8f0' : '1px solid #f1f5f9' }}>
                      {ci === 0 ? (
                        <>
                          <td rowSpan={product.consumedItems.length} style={{ ...td, verticalAlign: 'top', borderRight: '1px solid #e2e8f0' }}>{i + 1}</td>
                          <td rowSpan={product.consumedItems.length} style={{ ...td, fontWeight: 600, color: PRIMARY, verticalAlign: 'top', borderRight: '1px solid #e2e8f0' }}>{product.VoucherNumber || '-'}</td>
                          <td rowSpan={product.consumedItems.length} style={{ ...td, verticalAlign: 'top', borderRight: '1px solid #e2e8f0' }}>{fmtDate(product.ProductionDate)}</td>
                          <td rowSpan={product.consumedItems.length} style={{ ...td, fontWeight: 600, verticalAlign: 'top', borderRight: '1px solid #e2e8f0' }}>{product.ProductCode || '-'}</td>
                          <td rowSpan={product.consumedItems.length} style={{ ...td, color: '#1e293b', verticalAlign: 'top', borderRight: '1px solid #e2e8f0' }}>{product.ProductName || '-'}</td>
                          <td rowSpan={product.consumedItems.length} style={{ ...tdR, fontWeight: 700, verticalAlign: 'top', borderRight: '1px solid #e2e8f0' }}>{fmtQty(product.ProducedQty)}</td>
                        </>
                      ) : null}
                      <td style={{ ...td, color: PRIMARY, fontWeight: 500 }}>{c.ConsumedItemCode || '-'}</td>
                      <td style={td}>{c.ConsumedItemName || '-'}</td>
                      <td style={{ ...tdR, fontWeight: 600 }}>{fmtQty(c.ConsumedQty)}</td>
                    </tr>
                  ));
                })
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: PRIMARY_LT, color: PRIMARY, fontWeight: 700, position: 'sticky', bottom: 0 }}>
                  <td colSpan={5} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Totals</td>
                  <td style={tdR}>{fmtQty(totalProduced)}</td>
                  <td colSpan={2} style={{ ...td, textAlign: 'right', fontWeight: 700 }}></td>
                  <td style={tdR}>{fmtQty(totalConsumed)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProductionWiseStockReport;
